import { type FC, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { api } from '../../helpers/apiUtils';

/**
 * Angebot am Werbevideo (Vorfall 05.11.2026): Ein von Hand hochgeladenes Video lief ohne Kaufknopf, weil
 * nichts danach fragte. Der Dialog bietet das Angebot des passenden Webinars an, der Server baut den Link.
 * Regeln: `node-app/src/services/liveAutoRollAngebot.ts`.
 */
export type AngebotLage = 'ok' | 'kein_knopf' | 'knopf_aus' | 'knopf_zu_spaet' | 'bewusst_ohne';
export type AngebotErgebnis = 'angebot' | 'bewusst_ohne' | 'abgebrochen';

export const lageFehlt = (lage?: AngebotLage | null): boolean =>
  lage === 'kein_knopf' || lage === 'knopf_aus' || lage === 'knopf_zu_spaet';

const LAGE_TEXT: Record<Exclude<AngebotLage, 'ok'>, string> = {
  kein_knopf: 'Kein Angebot',
  knopf_aus: 'Angebot ausgeschaltet',
  knopf_zu_spaet: 'Angebot käme zu spät',
  bewusst_ohne: 'Bewusst ohne Angebot',
};

export const AngebotLageChip: FC<{ lage?: AngebotLage | null }> = ({ lage }) => {
  if (!lage || lage === 'ok') return null;
  return (
    <Chip
      size="small"
      label={LAGE_TEXT[lage]}
      color={lage === 'bewusst_ohne' ? 'default' : 'warning'}
      variant={lage === 'bewusst_ohne' ? 'outlined' : 'filled'}
    />
  );
};

interface Vorschlag {
  webinar_id: string;
  titel: string;
  titel_intern: string;
  datum: string;
  raum_dauer_sek: number | null;
  cta: { button_label: string; button_url: string; time_from_sec: number } | null;
  grund: string | null;
  laenge_passt: boolean;
  empfohlen: boolean;
}

interface VorschlagAntwort {
  success: boolean;
  video: { id: number; title: string; duration_sec: number | null };
  vorschlaege: Vorschlag[];
  mehrdeutig: boolean;
}

const uhr = (sek: number | null | undefined): string => {
  if (sek == null) return '–';
  const ganz = Math.max(0, Math.floor(sek));
  return `${Math.floor(ganz / 60)}:${String(ganz % 60).padStart(2, '0')}`;
};

/** `20:00` oder `1200` in Sekunden, sonst null. */
const leseZeit = (text: string): number | null => {
  const t = text.trim();
  const m = /^(\d{1,3}):([0-5]\d)$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  return /^\d{1,5}$/.test(t) ? Number(t) : null;
};

const tagAlsText = (datum: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : 'ohne Termin';
};

const fehlerText = (err: unknown, rueckfall: string): string => {
  const e = err as { response?: { data?: { error?: string; details?: string } }; message?: string };
  return e?.response?.data?.details || e?.response?.data?.error || e?.message || rueckfall;
};

export const AngebotDialog: FC<{
  video: { id: number; title: string } | null;
  meldung?: string | null;
  onFertig: (ergebnis: AngebotErgebnis) => void;
}> = ({ video, meldung, onFertig }) => {
  const [antwort, setAntwort] = useState<VorschlagAntwort | null>(null);
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string>('');
  const [startText, setStartText] = useState('');
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [ohneFrage, setOhneFrage] = useState(false);
  const [alleZeigen, setAlleZeigen] = useState(false);
  const videoId = video?.id ?? null;

  useEffect(() => {
    if (videoId == null) return undefined;
    let abgebrochen = false;
    setAntwort(null);
    setFehler(null);
    setGewaehlt('');
    setStartText('');
    setOhneFrage(false);
    setAlleZeigen(false);
    setLaden(true);
    api
      .get<VorschlagAntwort>(`/api/live/admin/ad-videos/${videoId}/angebot-vorschlaege`)
      .then((res) => {
        if (abgebrochen) return;
        setAntwort(res.data);
        // Vorausgewaehlt ist nur ein Webinar mit passender Laenge. Sonst muss jemand bewusst waehlen.
        const empfohlen = res.data.vorschlaege.find((v) => v.empfohlen);
        if (empfohlen?.cta) {
          setGewaehlt(empfohlen.webinar_id);
          setStartText(uhr(empfohlen.cta.time_from_sec));
        }
      })
      .catch((err) => {
        if (!abgebrochen) setFehler(fehlerText(err, 'Webinare konnten nicht geladen werden.'));
      })
      .finally(() => {
        if (!abgebrochen) setLaden(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, [videoId]);

  const auswahl = antwort?.vorschlaege.find((v) => v.webinar_id === gewaehlt) ?? null;
  const dauer = antwort?.video.duration_sec ?? null;
  const startSek = leseZeit(startText);
  const startFehler =
    startText.trim() === ''
      ? null
      : startSek == null
        ? 'Bitte als Minuten:Sekunden angeben, z. B. 20:00.'
        : dauer != null && startSek >= dauer
          ? `Der Knopf käme erst bei ${uhr(startSek)}, das Video endet bei ${uhr(dauer)}.`
          : null;
  const kannUebernehmen = !!auswahl?.cta && startSek != null && !startFehler && !beschaeftigt;
  // Kurz vor der Sendung zählt der eine Treffer. Der Rest steht auf Wunsch darunter.
  const vorschlaege = antwort?.vorschlaege ?? [];
  const sichtbar =
    vorschlaege.some((v) => v.empfohlen) && !alleZeigen
      ? vorschlaege.filter((v) => (v.laenge_passt && v.cta) || v.webinar_id === gewaehlt)
      : vorschlaege;
  const verborgen = vorschlaege.length - sichtbar.length;

  const uebernehmen = async () => {
    if (videoId == null || !auswahl || startSek == null) return;
    setBeschaeftigt(true);
    setFehler(null);
    try {
      // `ersetzen`: Der Dialog oeffnet nur bei fehlendem Angebot. Vorhandene Zeilen sind dann
      // ausgeschaltet oder kaemen zu spaet, sie wuerden den neuen Knopf sonst blockieren.
      await api.post(`/api/live/admin/ad-videos/${videoId}/angebot-aus-webinar`, {
        webinar_id: auswahl.webinar_id,
        time_from_sec: startSek,
        ersetzen: true,
      });
      onFertig('angebot');
    } catch (err) {
      setFehler(fehlerText(err, 'Das Angebot konnte nicht übernommen werden.'));
    } finally {
      setBeschaeftigt(false);
    }
  };

  const bewusstOhne = async () => {
    if (videoId == null) return;
    if (!ohneFrage) {
      setOhneFrage(true);
      return;
    }
    setBeschaeftigt(true);
    setFehler(null);
    try {
      await api.post(`/api/live/admin/ad-videos/${videoId}/ohne-angebot`, { bestaetigt: true });
      onFertig('bewusst_ohne');
    } catch (err) {
      setFehler(fehlerText(err, 'Das konnte nicht gespeichert werden.'));
    } finally {
      setBeschaeftigt(false);
    }
  };

  return (
    <Dialog open={video != null} onClose={() => onFertig('abgebrochen')} maxWidth="md" fullWidth>
      <DialogTitle>Angebot für „{video?.title}“</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {meldung || 'Während dieses Videos erscheint kein Kaufknopf.'}
        </Alert>
        {laden && (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CircularProgress size={18} />
            <Typography variant="body2">Passende Webinare werden geladen …</Typography>
          </Box>
        )}
        {fehler && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fehler}
          </Alert>
        )}
        {antwort && antwort.vorschlaege.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Es gibt in den letzten 180 Tagen kein Webinar mit fertigem Raum-Video. Der Knopf lässt
            sich im Reiter „Videos“ von Hand anlegen.
          </Alert>
        )}
        {antwort && antwort.vorschlaege.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Angebot wie im Webinar übernehmen
            </Typography>
            {!antwort.vorschlaege.some((v) => v.empfohlen) && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Kein Webinar hat dieselbe Länge wie dieses Video ({uhr(dauer)}). Bitte das richtige
                Webinar auswählen und prüfen, ab wann der Knopf erscheinen soll.
              </Alert>
            )}
            {antwort.mehrdeutig && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Mehrere Webinare haben diese Länge. Vorausgewählt ist das zuletzt gelaufene, bitte
                prüfen.
              </Alert>
            )}
            <RadioGroup
              value={gewaehlt}
              onChange={(e) => {
                const v = antwort.vorschlaege.find((x) => x.webinar_id === e.target.value);
                setGewaehlt(e.target.value);
                setStartText(v?.cta ? uhr(v.cta.time_from_sec) : '');
                setOhneFrage(false);
              }}
            >
              {sichtbar.map((v) => (
                <FormControlLabel
                  key={v.webinar_id}
                  value={v.webinar_id}
                  disabled={!v.cta}
                  sx={{ alignItems: 'flex-start', mb: 0.5 }}
                  control={<Radio size="small" sx={{ pt: 0.25 }} />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={v.empfohlen ? 700 : 400}>
                        {tagAlsText(v.datum)} · {v.titel}
                        {v.titel_intern && v.titel_intern !== v.titel ? ` (${v.titel_intern})` : ''}
                      </Typography>
                      {v.cta ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Knopf „{v.cta.button_label}“ ab {uhr(v.cta.time_from_sec)} ·{' '}
                          {v.laenge_passt
                            ? 'Länge passt'
                            : `Länge weicht ab (Webinar ${uhr(v.raum_dauer_sek)}, Video ${uhr(dauer)})`}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {v.grund}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
            {verborgen > 0 && (
              <Button
                size="small"
                onClick={() => setAlleZeigen(true)}
                sx={{ textTransform: 'none', display: 'block', mt: 0.5 }}
              >
                Anderes Webinar wählen ({verborgen})
              </Button>
            )}
            {auswahl?.cta && (
              <TextField
                size="small"
                label="Knopf erscheint ab (Minuten:Sekunden)"
                value={startText}
                onChange={(e) => setStartText(e.target.value)}
                error={!!startFehler}
                helperText={
                  startFehler || 'Vorgabe aus dem Webinar. Bei anders geschnittenem Video anpassen.'
                }
                sx={{ mt: 1.5, width: 340, maxWidth: '100%' }}
              />
            )}
          </>
        )}
        {ohneFrage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Das Video läuft dann ohne Kaufknopf, und es gibt keinen Alarm mehr dazu. Zum Bestätigen
            noch einmal auf „Bewusst ohne Angebot“ klicken.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button onClick={() => onFertig('abgebrochen')} disabled={beschaeftigt}>
          Abbrechen
        </Button>
        <Box flexGrow={1} />
        <Button color="warning" onClick={bewusstOhne} disabled={beschaeftigt}>
          Bewusst ohne Angebot
        </Button>
        <Button variant="contained" onClick={uebernehmen} disabled={!kannUebernehmen}>
          Angebot übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Hinweis im Reiter „Heute“: Das Video nach der heutigen Sendung zeigt kein Angebot. Letzte Stelle, an der
 * Moderator oder Marketing es vor der Sendung sehen. Die Abfrage scheitert still, sie ist nur ein Hinweis
 * und darf das Sendepult nicht stoeren.
 */
export const AutoRollAngebotHinweis: FC = () => {
  const [stand, setStand] = useState<{
    video: { id: number; title: string; verwaltet: boolean };
    details: string;
  } | null>(null);
  const [dialogOffen, setDialogOffen] = useState(false);

  const lade = useCallback(async () => {
    try {
      const res = await api.get<{
        video: { id: number; title: string; verwaltet: boolean } | null;
        lage: AngebotLage | null;
        details: string | null;
      }>('/api/live/admin/ad-videos/angebot-heute');
      const { video, lage, details } = res.data;
      setStand(video && lageFehlt(lage) ? { video, details: details || '' } : null);
    } catch {
      /* nur ein Hinweis */
    }
  }, []);

  useEffect(() => {
    void lade();
    const takt = window.setInterval(() => void lade(), 60_000);
    return () => window.clearInterval(takt);
  }, [lade]);

  if (!stand) return null;
  return (
    <>
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          stand.video.verwaltet ? undefined : (
            <Button color="inherit" size="small" onClick={() => setDialogOffen(true)}>
              Angebot festlegen
            </Button>
          )
        }
      >
        Nach der heutigen Sendung läuft ein Video ohne Kaufknopf. {stand.details}
        {stand.video.verwaltet &&
          ' Es ist die automatische Wiederholung: Bitte im Webinar das Angebot prüfen.'}
      </Alert>
      <AngebotDialog
        video={dialogOffen ? stand.video : null}
        meldung={stand.details}
        onFertig={() => {
          setDialogOffen(false);
          void lade();
        }}
      />
    </>
  );
};
